import { RotateCcw, Search, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '../../api';
import type { ContactRole, ContactSummary } from '../../api/areas/core';
import { DataTable, Field, FilterBar, Pager, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ContactRef, RegistryRef, RoleBadge, SubjectRef, useVocab } from '../domains/refs';
import { nextParams } from './shared';

const LIMIT = 50;
const ANY = '__any';
const ROLES: ContactRole[] = ['REGISTRANT', 'ADMIN', 'TECH', 'BILLING'];
const REGISTRIES = ['VNNIC', 'VERISIGN'];

/** `/contacts`: registry contact objects of the managed domains, and who they belong to. */
export function ContactsPage() {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation();
  const vocab = useVocab();
  useDocumentTitle(t('contacts.title'));
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const role = (params.get('role') ?? '') as ContactRole | '';
  const registry = params.get('registry') ?? '';
  const after = params.get('after') ?? '';
  const [draft, setDraft] = useState(q);
  const [draftFor, setDraftFor] = useState(q);
  const [history, setHistory] = useState<string[]>([]);
  const result = useApi(
    (signal) => api.listContacts({ q: q || undefined, role: role || undefined, registry: registry || undefined, after: after || undefined, limit: LIMIT }, { signal }),
    [q, role, registry, after],
  );
  if (draftFor !== q) {
    setDraftFor(q);
    setDraft(q);
  }
  const update = (next: Record<string, string>) => setParams(nextParams(params, next));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setHistory([]);
    update({ q: draft.trim(), after: '' });
  };
  const filtered = Boolean(q || role || registry);
  const reset = () => {
    setHistory([]);
    setDraft('');
    update({ q: '', role: '', registry: '', after: '' });
  };

  const columns: Column<ContactSummary>[] = [
    { key: 'contact', header: t('contacts.colContact'), cell: (c) => <ContactRef registry={c.registry_code} handle={c.handle} /> },
    { key: 'registry', header: t('contacts.colRegistry'), className: 'min-w-36 whitespace-normal', cell: (c) => <RegistryRef code={c.registry_code} name={c.registry_name} /> },
    {
      key: 'subject',
      header: t('contacts.colSubject'),
      cell: (c) =>
        c.subject_id ? (
          <SubjectRef id={c.subject_id} name={c.display_name} type={c.subject_type} />
        ) : (
          <span className="text-sm text-muted-foreground">{t('contacts.registrarContact')}</span>
        ),
    },
    {
      key: 'roles',
      header: t('contacts.colRoles'),
      cell: (c) =>
        c.roles.length ? (
          <span className="flex flex-wrap gap-1">
            {c.roles.map((r) => (
              <RoleBadge key={r} role={r} />
            ))}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t('contacts.noRoles')}</span>
        ),
    },
    {
      key: 'domains',
      header: t('contacts.colDomains'),
      numeric: true,
      cell: (c) => (
        <div>
          <div>{c.domain_count}</div>
          {c.total_domain_count !== c.domain_count ? <div className="text-xs text-muted-foreground">{t('contacts.totalDomains', { count: c.total_domain_count })}</div> : null}
        </div>
      ),
    },
    { key: 'assigned', header: t('contacts.colAssigned'), className: 'whitespace-nowrap', cell: (c) => formatDate(c.last_assigned_at) },
  ];

  return (
    <>
      <PageHeader title={t('contacts.title')} crumbs={[{ label: t('contacts.crumb') }]} subtitle={t('contacts.subtitle')} />
      <form onSubmit={submit} role="search">
        <FilterBar
          actions={
            filtered ? (
              <Button type="button" variant="ghost" size="sm" onClick={reset}>
                <RotateCcw /> {tc('action.reset')}
              </Button>
            ) : null
          }
        >
          <Field label={t('contacts.filterQuery')} className="w-full sm:w-72">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input type="search" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t('contacts.filterPlaceholder')} autoComplete="off" spellCheck={false} className="bg-card pl-8" />
            </div>
          </Field>
          <Field label={t('contacts.filterRole')} className="w-full sm:w-48">
            <Select
              value={role || ANY}
              onValueChange={(v) => {
                setHistory([]);
                update({ role: v === ANY ? '' : v, after: '' });
              }}
            >
              <SelectTrigger className="w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('contacts.anyRole')}</SelectItem>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {vocab('role', r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('contacts.filterRegistry')} className="w-full sm:w-48">
            <Select
              value={registry || ANY}
              onValueChange={(v) => {
                setHistory([]);
                update({ registry: v === ANY ? '' : v, after: '' });
              }}
            >
              <SelectTrigger className="w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('contacts.anyRegistry')}</SelectItem>
                {REGISTRIES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Button type="submit">
            <Search /> {t('contacts.search')}
          </Button>
        </FilterBar>
      </form>

      <Section flush title={t('contacts.tableTitle')} description={t('contacts.tableDescription')}>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={6} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable
              columns={columns}
              rows={result.data.items}
              rowKey={(c) => `${c.registry_code} ${c.handle}`}
              empty={
                <EmptyState title={t('contacts.emptyList')} icon={Users}>
                  {t('contacts.emptyListBody')}
                </EmptyState>
              }
            />
            {result.data.items.length > 0 || after ? (
              <Pager
                canPrevious={Boolean(after)}
                canNext={Boolean(result.data.next_after)}
                onPrevious={() => {
                  const prev = history.slice(0, -1);
                  update({ after: history[history.length - 1] ?? '' });
                  setHistory(prev);
                }}
                onNext={() => {
                  setHistory([...history, after]);
                  update({ after: result.data.next_after ?? '' });
                }}
                info={t('contacts.shown', { count: result.data.items.length })}
              />
            ) : null}
          </>
        ) : null}
      </Section>
    </>
  );
}
